import { useState, useCallback, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import styles from "./SignUp.module.css";
import AuthLeftPanel from "../../common/AuthLeftPanel";
import ComplianceBadges from "../../common/ComplianceBadge";
import { REDIRECT_AVEXA_BASE_URL } from "../../../config";
import { clearAllStorage, storePatientSession } from "../../../utils/storage/authStorage";
import {
  apiSendOtp,
  apiSetPassword,
  apiRegister,
  apiRegVerifyOtp,
} from "../../../api/authApi";

/* ─── Validation ─────────────────────────────────────────────────── */
function validateStep1(fields) {
  const errors = {};
  if (!fields.fullName?.trim()) errors.fullName = "Full name is required";
  if (!fields.dob?.trim()) errors.dob = "Date of birth is required";

  const mobile = String(fields.mobile ?? "").replace(/\s/g, "");
  if (!mobile) errors.mobile = "Mobile number is required";
  else if (!/^\d{10}$/.test(mobile)) errors.mobile = "Enter a valid 10-digit number";

  if (fields.email?.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fields.email)) {
    errors.email = "Enter a valid email address";
  }

  return errors;
}

function validatePasswords(password, confirmPassword) {
  const errors = {};
  if (!password) errors.password = "Password is required";
  else if (password.length < 8) errors.password = "Minimum 8 characters";
  if (!confirmPassword) errors.confirmPassword = "Please confirm password";
  else if (confirmPassword !== password) errors.confirmPassword = "Passwords do not match";
  return errors;
}

/* ─── Sub-components (all from file 2, unchanged) ────────────────── */
function FormField({ label, error, required, children }) {
  return (
    <div className={styles.field}>
      {label && (
        <label className={`ava-text-body ${styles.label} ${required ? styles.required : ""}`}>
          {label}
        </label>
      )}
      {children}
      {error && <span className={styles.errorMsg}>{error}</span>}
    </div>
  );
}

function TextInput({ name, value, onChange, placeholder, type = "text", hasError }) {
  return (
    <input
      className={`${styles.input} ${hasError ? styles.inputError : ""}`}
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      autoComplete="off"
    />
  );
}

function PasswordInput({ name, value, onChange, placeholder, hasError }) {
  const [show, setShow] = useState(false);
  return (
    <div className={`${styles.passwordWrap} ${hasError ? styles.inputError : ""}`}>
      <input
        className={styles.passwordInner}
        type={show ? "text" : "password"}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        autoComplete="new-password"
      />
      <button
        type="button"
        className={`ava-text-caption ${styles.showBtn}`}
        onClick={() => setShow((s) => !s)}
      >
        {show ? "Hide" : "Show"}
      </button>
    </div>
  );
}

function GenderSelector({ value, onChange }) {
  const opts = ["Female", "Male", "Other"];
  return (
    <div className={styles.genderGroup}>
      {opts.map((g) => (
        <button
          key={g}
          type="button"
          onClick={() => onChange(g)}
          className={`ava-text-caption ${styles.genderBtn} ${value === g ? styles.genderBtnActive : ""}`}
        >
          {g === "Female" && (
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <circle cx="12" cy="8" r="5" />
              <line x1="12" y1="13" x2="12" y2="21" />
              <line x1="9" y1="18" x2="15" y2="18" />
            </svg>
          )}
          {g}
        </button>
      ))}
    </div>
  );
}

function Notification({ type, message, onClose }) {
  return (
    <div className={`${styles.notif} ${type === "success" ? styles.notifSuccess : styles.notifError}`}>
      {message}
      <button className={styles.notifClose} onClick={onClose}>×</button>
    </div>
  );
}

/* ─── OTP Input ──────────────────────────────────────────────────── */
function OtpInput({ value, onChange, hasError }) {
  const refs = useRef([]);
  const digits = value.split("");

  const handleKey = (e, idx) => {
    if (e.key === "Backspace") {
      if (digits[idx]) {
        const next = [...digits];
        next[idx] = "";
        onChange(next.join(""));
      } else if (idx > 0) {
        refs.current[idx - 1]?.focus();
      }
    } else if (e.key === "ArrowLeft" && idx > 0) {
      refs.current[idx - 1]?.focus();
    } else if (e.key === "ArrowRight" && idx < 5) {
      refs.current[idx + 1]?.focus();
    }
  };

  const handleChange = (e, idx) => {
    const v = e.target.value.replace(/\D/g, "").slice(-1);
    const next = [...digits];
    next[idx] = v;
    for (let i = 0; i < 6; i++) if (!next[i]) next[i] = "";
    onChange(next.join(""));
    if (v && idx < 5) refs.current[idx + 1]?.focus();
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const paste = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    onChange(paste.padEnd(6, "").slice(0, 6));
    refs.current[Math.min(paste.length, 5)]?.focus();
  };

  return (
    <div className={styles.otpGrid}>
      {[0, 1, 2, 3, 4, 5].map((idx) => (
        <input
          key={idx}
          ref={(el) => (refs.current[idx] = el)}
          className={`${styles.otpBox} ${hasError ? styles.otpBoxError : ""} ${digits[idx] ? styles.otpBoxFilled : ""}`}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={digits[idx] || ""}
          onChange={(e) => handleChange(e, idx)}
          onKeyDown={(e) => handleKey(e, idx)}
          onPaste={handlePaste}
        />
      ))}
    </div>
  );
}

/* ─── Resend timer ───────────────────────────────────────────────── */
function ResendTimer({ onResend }) {
  const [seconds, setSeconds] = useState(30);
  const [canResend, setCanResend] = useState(false);

  useEffect(() => {
    if (seconds <= 0) { setCanResend(true); return; }
    const t = setTimeout(() => setSeconds((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [seconds]);

  const handleResend = () => {
    setSeconds(30);
    setCanResend(false);
    onResend();
  };

  return (
    <div className={styles.resendRow}>
      {canResend ? (
        <button type="button" className={`ava-text-caption ${styles.resendBtn}`} onClick={handleResend}>
          Resend OTP
        </button>
      ) : (
        <span className={`ava-text-caption ${styles.resendTimer}`}>
          Resend in <strong>{seconds}s</strong>
        </span>
      )}
    </div>
  );
}

/* ─── Shared registration form fields (file 2's component, unchanged) */
function RegistrationFields({ values, errors, onChange, tab }) {
  return (
    <>
      <div className={styles.row2}>
        <FormField label="Full Name" error={errors.fullName} required>
          <TextInput
            name="fullName" value={values.fullName} onChange={onChange}
            placeholder="Ananya Sharma" hasError={!!errors.fullName}
          />
        </FormField>

        <FormField label="Date of Birth" error={errors.dob} required>
          <div className={styles.inputIconWrap}>
            <input
              className={`${styles.input} ${errors.dob ? styles.inputError : ""}`}
              type="date" name="dob" value={values.dob} onChange={onChange}
              min="1947-01-01"
              max={new Date().toISOString().split("T")[0]}
            />
          </div>
        </FormField>
      </div>

      <div className={styles.row2}>
        <FormField label="Mobile Number" error={errors.mobile} required>
          <div className={styles.mobileWrap}>
            <span className={`ava-card-bg ava-text-caption ${styles.dialCode}`}>+91</span>
            <input
              className={`${styles.mobileInput} ${errors.mobile ? styles.inputError : ""}`}
              type="tel" name="mobile" value={values.mobile} onChange={onChange}
              placeholder="98765 43210" maxLength={10}
              onKeyPress={(e) => { if (!/\d/.test(e.key)) e.preventDefault(); }}
            />
          </div>
        </FormField>

        {/* Email is optional in the new flow */}
        <FormField label="Email Address" error={errors.email}>
          <TextInput
            name="email" value={values.email} onChange={onChange}
            placeholder="ananya.sharma@email.com" type="email" hasError={!!errors.email}
          />
        </FormField>
      </div>

      {tab === "abha" && (
        <div className={styles.abhaSection}>
          <div className={styles.abhaSectionHeader}>
            <div>
              <p className={`ava-text-subheading ${styles.abhaSectionTitle}`}>
                Link your health ID (ABHA)
              </p>
              <p className={`ava-text-caption ${styles.abhaSectionSub}`}>
                Recommended for a seamless experience
              </p>
            </div>
            <button type="button" className={`ava-text-body ${styles.abhaWhatBtn}`}>
              ⓘ What is ABHA?
            </button>
          </div>
          <div className={`${styles.inputIconWrap} ${styles.abhaInputWrap}`}>
            <span className={`ava-text-caption ava-card-bg ${styles.abhaTag}`}>ABHA</span>
            <input
              className={styles.abhaInput}
              type="text" name="abhaNumber" value={values.abhaNumber} onChange={onChange}
              placeholder="Enter your 14-digit ABHA number" maxLength={14}
              onKeyPress={(e) => { if (!/\d/.test(e.key)) e.preventDefault(); }}
            />
            <span className={styles.abhaOptional}>Optional</span>
          </div>
          {errors.abhaNumber && <span className={styles.errorMsg}>{errors.abhaNumber}</span>}
        </div>
      )}
    </>
  );
}

/* ─── Mobile bottom card (file 2, unchanged) ─────────────────────── */
function MobileBottomCard() {
  return (
    <div className={styles.mobileBottomCard}>
      <div className={styles.mobileTrustBadge}>
        <div className={styles.mobileTrustIcon}>
          <img
            src="/images/tab4.svg" alt="" width={18} height={18}
            onError={(e) => { e.currentTarget.style.opacity = "0"; }}
          />
        </div>
        <div>
          <p className={styles.mobileTrustTitle}>Trusted by 2M+ patients</p>
          <p className={styles.mobileTrustSub}>across 1,000+ care centers</p>
        </div>
      </div>
      <div className={styles.mobilePhotoWrap}>
        <img
          src="/images/family.svg"
          alt="Family using health app"
          className={styles.mobilePhotoImg}
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
      </div>
      <div className={styles.mobileCompliance}>
        <ComplianceBadges />
      </div>
    </div>
  );
}

/* ─── MAIN SignUp ─────────────────────────────────────────────────── */
// Steps: "register" | "otp" | "setPassword" | "success"
export default function SignUp() {
  const [step, setStep] = useState("register");
  const [activeTab, setActiveTab] = useState("manual");
  const [loading, setLoading] = useState(false);
  const [notif, setNotif] = useState(null);
  const [agreed, setAgreed] = useState(false);
  const [errors, setErrors] = useState({});

  // Step 1 fields — no password fields (moved to step 3)
  const initValues = { fullName: "", dob: "", mobile: "", email: "", abhaNumber: "" };
  const [values, setValues] = useState(initValues);

  // Step 2 — OTP
  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState("");

  // Step 3 — password
  const [pwValues, setPwValues] = useState({ password: "", confirmPassword: "" });
  const [pwErrors, setPwErrors] = useState({});

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setValues((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  }, []);

  const handlePwChange = useCallback((e) => {
    const { name, value } = e.target;
    setPwValues((prev) => ({ ...prev, [name]: value }));
    setPwErrors((prev) => ({ ...prev, [name]: undefined }));
  }, []);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setErrors({});
  };

  /* ── Step 1: Validate → Send OTP ── */
  const handleRegisterAndSendOtp = async (e) => {
    e.preventDefault();
    if (!agreed) {
      setNotif({ type: "error", message: "Please agree to Terms & Privacy Policy" });
      return;
    }

    const errs = validateStep1(values);
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }

    const mobile = String(values.mobile ?? "").replace(/\s/g, "");

    setLoading(true);
    try {
      await apiSendOtp(mobile);
      setNotif({ type: "success", message: "OTP sent successfully!" });
      setOtp("");
      setOtpError("");
      setStep("otp");
    } catch (err) {
      setNotif({ type: "error", message: err.message || "Failed to send OTP" });
    } finally {
      setLoading(false);
    }
  };

  /* ── Step 2: Verify OTP → Register ── */
  const handleVerifyOtp = async (e) => {
    e.preventDefault();






    
    if (otp.length !== 6) {
      setOtpError("Please enter all 6 digits");
      return;
    }

    const mobile = String(values.mobile ?? "").replace(/\s/g, "");
    const [y, m, d] = values.dob.split("-");
    const formattedDob = `${parseInt(d)}-${parseInt(m)}-${y}`;

    setLoading(true);
    try {
      await apiRegVerifyOtp(mobile, otp);
      await apiRegister({
        fullName: values.fullName,
        dob: values.dob,
        mobileNumber: mobile,
        orgName: "Mukta Women's Clinic",
        ...(values.email?.trim() ? { email: values.email.trim() } : {}),
        consent: true,
        ...(activeTab === "abha" && values.abhaNumber ? { abhaNumber: values.abhaNumber } : {}),
      });
      setPwValues({ password: "", confirmPassword: "" });
      setPwErrors({});
      setStep("setPassword");
    } catch (err) {
      setOtpError(err.message || "Invalid OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    const mobile = String(values.mobile ?? "").replace(/\s/g, "");
    try {
      await apiSendOtp(mobile);
      setNotif({ type: "success", message: "OTP resent successfully!" });
      setOtp("");
      setOtpError("");
    } catch (err) {
      setNotif({ type: "error", message: err.message || "Failed to resend OTP" });
    }
  };

  /* ── Step 3: Set Password ── */
  const handleSetPassword = async (e) => {
    e.preventDefault();
    const errs = validatePasswords(pwValues.password, pwValues.confirmPassword);
    if (Object.keys(errs).length > 0) { setPwErrors(errs); return; }

    setLoading(true);
    try {
      const response = await apiSetPassword({
        mobileNumber: values.mobile,
        password: pwValues.password,
        orgName: "Mukta Women's Clinic",
        dob: values.dob,
        confirmPassword: pwValues.confirmPassword,
      });
      await clearAllStorage();
      if (response.success) {
        setNotif({ type: "success", message: response.message || "Password set successfully!" });
        storePatientSession({
          ...response.data,
           patientId:response?.data?.patientId,
          fullName:  values.fullName ?? response.data.userName,
          mobile: values.mobile ?? response.data.mobileNumber,
          orgName: "Mukta Women's Clinic",
          dob: values.dob,
          isVerified: true,
        });
      }
      await new Promise((r) => setTimeout(r, 800));
      setStep("success");
    } catch (err) {
      setNotif({ type: "error", message: err.message || "Failed to set password" });
    } finally {
      setLoading(false);
    }
  };

  /* ── Step titles ── */
  const stepMeta = {
    register:    { title: "Create your account",  sub: "Start your journey to better health." },
    otp:         { title: "Verify your mobile",   sub: `Enter the 6-digit OTP sent to +91 ${values.mobile}` },
    setPassword: { title: "Set your password",    sub: "Secure your OneCare account." },
    success:     { title: "You're all set!",       sub: "Your account has been created and verified." },
  };

  const { title, sub } = stepMeta[step];

  return (
    <div className={styles.page}>

      {/* ── Left panel ── */}
      <aside className={`ava-card-bg ${styles.leftPanel}`}>
        <AuthLeftPanel />
      </aside>

      {/* ── Right panel ── */}
      <main className={styles.rightPanel}>

        {notif && (
          <Notification
            type={notif.type}
            message={notif.message}
            onClose={() => setNotif(null)}
          />
        )}

        <div className={styles.formCard}>

          {/* Card header */}
          <div className={styles.cardHeader}>
            <div>
              <h2 className={`ava-heading-lg ${styles.cardTitle}`}>{title}</h2>
              <p className={`ava-text-body ${styles.cardSub}`}>{sub}</p>
            </div>
            {step === "register" && (
              <div>
                <p className={`ava-text-caption ${styles.footer}`}>
                  Need help?{" "}
                  <a href="mailto:support@avexa.com" className={styles.footerLink}>
                    Contact support
                  </a>
                </p>
              </div>
            )}
          </div>

          {/* ══ STEP 1 — Register ══ */}
          {step === "register" && (
            <>
              {/* Tab strip (file 2 markup) */}
              <div className={styles.tabStrip}>
                <button
                  type="button"
                  className={`${styles.tabBtn} ava-text-subheading ${activeTab === "manual" ? styles.tabBtnActive : ""}`}
                  onClick={() => handleTabChange("manual")}
                >
                  Register Manually
                </button>
                <button
                  type="button"
                  className={`${styles.tabBtn} ${activeTab === "abha" ? styles.tabBtnActive : ""}`}
                  onClick={() => handleTabChange("abha")}
                >
                  Register with ABHA
                </button>
              </div>

              <form className={styles.form} onSubmit={handleRegisterAndSendOtp} noValidate>

                {activeTab === "manual" && (
                  <RegistrationFields
                    values={values}
                    errors={errors}
                    onChange={handleChange}
                    tab={activeTab}
                  />
                )}

                {activeTab === "abha" && (
                  <div className={styles.abhaSection}>
                    <div className={styles.abhaSectionHeader}>
                      <div>
                        <p className={styles.abhaSectionTitle}>Link your health ID (ABHA)</p>
                        <p className={styles.abhaSectionSub}>Recommended for a seamless experience</p>
                      </div>
                      <button type="button" className={`ava-text-caption ${styles.abhaWhatBtn}`}>
                        ⓘ What is ABHA?
                      </button>
                    </div>
                    <div className={`${styles.inputIconWrap} ${styles.abhaInputWrap}`}>
                      <span className={`ava-text-caption ava-card-bg ${styles.abhaTag}`}>ABHA</span>
                      <input
                        className={styles.abhaInput}
                        type="text" name="abhaNumber" value={values.abhaNumber}
                        onChange={handleChange}
                        placeholder="Enter your 14-digit ABHA number"
                        maxLength={14}
                        onKeyPress={(e) => { if (!/\d/.test(e.key)) e.preventDefault(); }}
                      />
                      <span className={styles.abhaOptional}>Optional</span>
                    </div>
                    {errors.abhaNumber && (
                      <span className={styles.errorMsg}>{errors.abhaNumber}</span>
                    )}
                  </div>
                )}

                {/* Terms + Submit */}
                <div className={styles.formFooter}>
                  <label className={styles.termsRow}>
                    <input
                      type="checkbox" className={styles.checkbox}
                      checked={agreed} onChange={(e) => setAgreed(e.target.checked)}
                    />
                    <span className="ava-text-caption">
                      I agree to the{" "}
                      <Link to="#" className={styles.termsLink}>Terms of Use</Link>
                      {" "}and{" "}
                      <Link to="#" className={styles.termsLink}>Privacy Policy</Link>
                    </span>
                  </label>

                  <button type="submit" className={styles.submitBtn} disabled={loading}>
                    {loading ? "Sending OTP…" : "Create Account & Send OTP"}
                  </button>
                </div>
              </form>

              <p className={`ava-text-caption ${styles.footerText}`}>
                Already have an account?{" "}
                <Link to="/sign-in" className={styles.termsLink}>Sign in</Link>
              </p>
            </>
          )}

          {/* ══ STEP 2 — OTP ══ */}
          {step === "otp" && (
            <form className={styles.form} onSubmit={handleVerifyOtp} noValidate>
              <OtpInput value={otp} onChange={setOtp} hasError={!!otpError} />
              {otpError && <span className={`${styles.errorMsg} ${styles.otpErrorCenter}`}>{otpError}</span>}

              <ResendTimer onResend={handleResendOtp} />

              <div className={styles.formFooter}>
                <button type="submit" className={styles.submitBtn} disabled={loading}>
                  {loading ? "Verifying…" : "Verify OTP"}
                </button>
                <button
                  type="button"
                  className={styles.backBtn}
                  onClick={() => setStep("register")}
                  disabled={loading}
                >
                  ← Back
                </button>
              </div>
            </form>
          )}

          {/* ══ STEP 3 — Set Password ══ */}
          {step === "setPassword" && (
            <form className={styles.form} onSubmit={handleSetPassword} noValidate>
              <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
                <FormField label="Create Password" error={pwErrors.password} required>
                  <PasswordInput
                    name="password" value={pwValues.password} onChange={handlePwChange}
                    placeholder="Min 8 characters" hasError={!!pwErrors.password}
                  />
                </FormField>

                <FormField label="Confirm Password" error={pwErrors.confirmPassword} required>
                  <PasswordInput
                    name="confirmPassword" value={pwValues.confirmPassword} onChange={handlePwChange}
                    placeholder="Re-enter password" hasError={!!pwErrors.confirmPassword}
                  />
                </FormField>
              </div>

              <div className={styles.formFooter}>
                <button type="submit" className={styles.submitBtn} disabled={loading}>
                  {loading ? "Setting password…" : "Set Password & Continue"}
                </button>
              </div>
            </form>
          )}

          {/* ══ STEP 4 — Success ══ */}
          {step === "success" && (
            <div className={styles.successWrap}>
              <div className={styles.successIcon}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none"
                  stroke="var(--primary-color, #00246B)" strokeWidth="2.5"
                  strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 6L9 17l-5-5" />
                </svg>
              </div>
              <p className={`ava-text-body ${styles.successMsg}`}>
                Your account has been verified successfully. You can now sign in to OneCare.
              </p>
              <Link
                to="/sign-in"
                className={styles.submitBtn}
                style={{ textAlign: "center", display: "block", textDecoration: "none", marginTop: "1rem" }}
              >
                Go to Sign In
              </Link>
            </div>
          )}

        </div>

        {/* Mobile photoCard + compliance */}
        <MobileBottomCard />

      </main>
    </div>
  );
}


// import { useState, useCallback } from "react";
// import { Link } from "react-router-dom";
// import styles from "./SignUp.module.css";
// import AuthLeftPanel from "../../common/AuthLeftPanel";
// import ComplianceBadges from "../../common/ComplianceBadge";
// import { registerUser } from "../../../api/authApi";
// import { REDIRECT_AVEXA_BASE_URL } from "../../../config";
// import { encryptPassword } from "../../../utils";

// /* ─── Validation ─────────────────────────────────────────────────── */
// function validate(fields, tab) {
//   const errors = {};
//   if (!fields.fullName?.trim()) errors.fullName = "Full name is required";
//   if (!fields.dob?.trim()) errors.dob = "Date of birth is required";
//   // if (!fields.gender) errors.gender = "Please select a gender";

//   const mobile = String(fields.mobile ?? "").replace(/\s/g, "");
//   if (!mobile) errors.mobile = "Mobile number is required";
//   else if (!/^\d{10}$/.test(mobile)) errors.mobile = "Enter a valid 10-digit number";

//   if (!fields.email?.trim()) {
//     errors.email = "Email is required";
//   } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fields.email)) {
//     errors.email = "Enter a valid email address";
//   }

//   if (!fields.password) errors.password = "Password is required";
//   else if (fields.password.length < 8) errors.password = "Minimum 8 characters";

//   if (!fields.confirmPassword) errors.confirmPassword = "Please confirm password";
//   else if (fields.confirmPassword !== fields.password)
//     errors.confirmPassword = "Passwords do not match";

//   if (
//     tab === "abha" &&
//     fields.abhaNumber &&
//     !/^\d{14}$/.test(fields.abhaNumber.replace(/\s/g, ""))
//   ) {
//     errors.abhaNumber = "ABHA number must be 14 digits";
//   }

//   return errors;
// }

// /* ─── Sub-components ─────────────────────────────────────────────── */
// function FormField({ label, error, required, children }) {
//   return (
//     <div className={styles.field}>
//       {label && (
//         <label className={`ava-text-body ${styles.label} ${required ? styles.required : ""}`}>
//           {label}
//         </label>
//       )}
//       {children}
//       {error && <span className={styles.errorMsg}>{error}</span>}
//     </div>
//   );
// }

// function TextInput({ name, value, onChange, placeholder, type = "text", hasError }) {
//   return (
//     <input
//       className={`${styles.input} ${hasError ? styles.inputError : ""}`}
//       type={type}
//       name={name}
//       value={value}
//       onChange={onChange}
//       placeholder={placeholder}
//       autoComplete="off"
//     />
//   );
// }

// function PasswordInput({ name, value, onChange, placeholder, hasError }) {
//   const [show, setShow] = useState(false);
//   return (
//     <div className={`${styles.passwordWrap} ${hasError ? styles.inputError : ""}`}>
//       <input
//         className={styles.passwordInner}
//         type={show ? "text" : "password"}
//         name={name}
//         value={value}
//         onChange={onChange}
//         placeholder={placeholder}
//         autoComplete="new-password"
//       />
//       <button
//         type="button"
//         className={`ava-text-caption ${styles.showBtn}`}
//         onClick={() => setShow((s) => !s)}
//       >
//         {show ? "Hide" : "Show"}
//       </button>
//     </div>
//   );
// }

// function GenderSelector({ value, onChange }) {
//   const opts = ["Female", "Male", "Other"];
//   return (
//     <div className={styles.genderGroup}>
//       {opts.map((g) => (
//         <button
//           key={g}
//           type="button"
//           onClick={() => onChange(g)}
//           className={`ava-text-caption ${styles.genderBtn} ${value === g ? styles.genderBtnActive : ""}`}
//         >
//           {g === "Female" && (
//             <svg
//               width="13" height="13" viewBox="0 0 24 24"
//               fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
//             >
//               <circle cx="12" cy="8" r="5" />
//               <line x1="12" y1="13" x2="12" y2="21" />
//               <line x1="9" y1="18" x2="15" y2="18" />
//             </svg>
//           )}
//           {g}
//         </button>
//       ))}
//     </div>
//   );
// }

// function Notification({ type, message, onClose }) {
//   return (
//     <div
//       className={`${styles.notif} ${type === "success" ? styles.notifSuccess : styles.notifError}`}
//     >
//       {message}
//       <button className={styles.notifClose} onClick={onClose}>×</button>
//     </div>
//   );
// }

// /* ─── Shared registration form fields ────────────────────────────── */
// function RegistrationFields({ values, errors, onChange, tab }) {
//   return (
//     <>
//       {/* Row 1: Full Name + DOB */}
//       <div className={styles.row2}>
//         <FormField label="Full Name" error={errors.fullName} required>
//           <TextInput
//             name="fullName" value={values.fullName} onChange={onChange}
//             placeholder="Ananya Sharma" hasError={!!errors.fullName}
//           />
//         </FormField>

//         <FormField label="Date of Birth" error={errors.dob} required>
//           <div className={styles.inputIconWrap}>
//             <input
//               className={`${styles.input} ${errors.dob ? styles.inputError : ""}`}
//               type="date" name="dob" value={values.dob} onChange={onChange}
//             />
//           </div>
//         </FormField>
//       </div>

//       {/* Row 2: Gender */}
//       {/* <div className={styles.genderRow}>
//         <span className={`ava-text-body ${styles.label} ${styles.required}`}>Gender</span>
//         <GenderSelector value={values.gender} onChange={onGender} />
//         {errors.gender && <span className={styles.errorMsg}>{errors.gender}</span>}
//       </div> */}

//       {/* Row 3: Mobile + Email */}
//       <div className={styles.row2}>
//         <FormField label="Mobile Number" error={errors.mobile} required>
//           <div className={styles.mobileWrap}>
//             <span className={`ava-card-bg ava-text-caption ${styles.dialCode}`}>+91</span>
//             <input
//               className={`${styles.mobileInput} ${errors.mobile ? styles.inputError : ""}`}
//               type="tel" name="mobile" value={values.mobile} onChange={onChange}
//               placeholder="98765 43210" maxLength={10}
//               onKeyPress={(e) => { if (!/\d/.test(e.key)) e.preventDefault(); }}
//             />
//           </div>
//         </FormField>

//         <FormField label="Email Address" error={errors.email} required>
//           <TextInput
//             name="email" value={values.email} onChange={onChange}
//             placeholder="ananya.sharma@email.com" type="email" hasError={!!errors.email}
//           />
//         </FormField>
//       </div>

//       {/* Row 4: Password + Confirm */}
//       <div className={styles.row2}>
//         <FormField label="Create Password" error={errors.password} required>
//           <PasswordInput
//             name="password" value={values.password} onChange={onChange}
//             placeholder="Min 8 characters" hasError={!!errors.password}
//           />
//         </FormField>

//         <FormField label="Confirm Password" error={errors.confirmPassword} required>
//           <PasswordInput
//             name="confirmPassword" value={values.confirmPassword} onChange={onChange}
//             placeholder="Re-enter password" hasError={!!errors.confirmPassword}
//           />
//         </FormField>
//       </div>

//       {/* ABHA — only on ABHA tab */}
//       {tab === "abha" && (
//         <div className={styles.abhaSection}>
//           <div className={styles.abhaSectionHeader}>
//             <div>
//               <p className={`ava-text-subheading ${styles.abhaSectionTitle}`}>
//                 Link your health ID (ABHA)
//               </p>
//               <p className={`ava-text-caption ${styles.abhaSectionSub}`}>
//                 Recommended for a seamless experience
//               </p>
//             </div>
//             <button type="button" className={`ava-text-body ${styles.abhaWhatBtn}`}>
//               ⓘ What is ABHA?
//             </button>
//           </div>
//           <div className={`${styles.inputIconWrap} ${styles.abhaInputWrap}`}>
//             <span className={styles.abhaTag}>ABHA</span>
//             <input
//               className={styles.abhaInput}
//               type="text" name="abhaNumber" value={values.abhaNumber} onChange={onChange}
//               placeholder="Enter your 14-digit ABHA number" maxLength={14}
//               onKeyPress={(e) => { if (!/\d/.test(e.key)) e.preventDefault(); }}
//             />
//             <span className={styles.abhaOptional}>Optional</span>
//           </div>
//           {errors.abhaNumber && <span className={styles.errorMsg}>{errors.abhaNumber}</span>}
//         </div>
//       )}
//     </>
//   );
// }

// /* ─── Mobile bottom card (photoCard + compliance) ────────────────── */
// function MobileBottomCard() {
//   return (
//     <div className={styles.mobileBottomCard}>
//       {/* Mini trust badge */}
//       <div className={styles.mobileTrustBadge}>
//         <div className={styles.mobileTrustIcon}>
//           <img
//             src="/images/tab4.svg" alt="" width={18} height={18}
//             onError={(e) => { e.currentTarget.style.opacity = "0"; }}
//           />
//         </div>
//         <div>
//           <p className={styles.mobileTrustTitle}>Trusted by 2M+ patients</p>
//           <p className={styles.mobileTrustSub}>across 1,000+ care centers</p>
//         </div>
//       </div>

//       {/* Photo image */}
//       <div className={styles.mobilePhotoWrap}>
//         <img
//           src="/images/family.svg"
//           alt="Family using health app"
//           className={styles.mobilePhotoImg}
//           onError={(e) => { e.currentTarget.style.display = "none"; }}
//         />
//       </div>

//       {/* Compliance badges */}
//       <div className={styles.mobileCompliance}>
//         <ComplianceBadges />
//       </div>
//     </div>
//   );
// }

// /* ─── MAIN SignUp ─────────────────────────────────────────────────── */
// export default function SignUp() {
//   const [activeTab, setActiveTab] = useState("manual");
//   const [loading, setLoading] = useState(false);
//   const [notif, setNotif] = useState(null);
//   const [agreed, setAgreed] = useState(false);

//   // const initValues = {
//   //   fullName: "", dob: "", gender: "", mobile: "", email: "",
//   //   password: "", confirmPassword: "", abhaNumber: "",
//   // };
//    const initValues = {
//     fullName: "", dob: "",  mobile: "", email: "",
//     password: "", confirmPassword: "", abhaNumber: "",
//   };
//   const [values, setValues] = useState(initValues);
//   const [errors, setErrors] = useState({});

//   const handleChange = useCallback((e) => {
//     const { name, value } = e.target;
//     setValues((prev) => ({ ...prev, [name]: value }));
//     setErrors((prev) => ({ ...prev, [name]: undefined }));
//   }, []);

//   // const handleGender = (g) => {
//   //   setValues((prev) => ({ ...prev, gender: g }));
//   //   setErrors((prev) => ({ ...prev, gender: undefined }));
//   // };

//   const handleTabChange = (tab) => {
//     setActiveTab(tab);
//     setErrors({});
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     if (!agreed) {
//       setNotif({ type: "error", message: "Please agree to Terms & Privacy Policy" });
//       return;
//     }

//     const errs = validate(values, activeTab);
//     if (Object.keys(errs).length > 0) { setErrors(errs); return; }

//     const mobile = String(values.mobile ?? "").replace(/\s/g, "");
//     const payload = {
//       userName: values.fullName,
//       email: values.email,
//       mobileNumber: Number(mobile),
//       password: values.password,
//       confirmPassword: values.confirmPassword,
//       ...(activeTab === "abha" && values.abhaNumber ? { abhaNumber: values.abhaNumber } : {}),
//     };

//     setLoading(true);
//     try {
//       const data = await registerUser(payload);
//       if (!data?.success) {
//         setNotif({ type: "error", message: data?.message || "Registration failed" });
//         return;
//       }
//       setNotif({ type: "success", message: data?.message || "Account created successfully!" });
//       const secret = await encryptPassword(values.password);
//       const redirectUrl = new URL(REDIRECT_AVEXA_BASE_URL);
//       redirectUrl.searchParams.set("email", values.email);
//       redirectUrl.searchParams.set("secret", secret);
//       setValues(initValues);
//       setTimeout(() => { window.location.href = redirectUrl.toString(); }, 1200);
//     } catch (err) {
//       setNotif({ type: "error", message: err.message || "Registration failed" });
//     } finally {
//       setLoading(false);
//     }
//   };

//   return (
//     <div className={styles.page}>

//       {/* ── Left panel (desktop sidebar) ── */}
//       <aside className={`ava-card-bg ${styles.leftPanel}`}>
//         <AuthLeftPanel />
//       </aside>

//       {/* ── Right panel ── */}
//       <main className={styles.rightPanel}>

//         {notif && (
//           <Notification
//             type={notif.type}
//             message={notif.message}
//             onClose={() => setNotif(null)}
//           />
//         )}

//         <div className={styles.formCard}>

//           {/* Card header */}
//           <div className={styles.cardHeader}>
//             <div>
//               <h2 className={`ava-heading-lg ${styles.cardTitle}`}>Create your account</h2>
//               <p className={`ava-text-body ${styles.cardSub}`}>Start your journey to better health.</p>
//             </div>
//             <div>
//               <p className={`ava-text-caption ${styles.footer}`}>
//                 Need help?{" "}
//                 <a href="mailto:support@avexa.com" className={styles.footerLink}>
//                   Contact support
//                 </a>
//               </p>
//             </div>
//           </div>

//           {/* Tab strip */}
//           <div className={`${styles.tabStrip}`}>
//             <button
//               type="button"
//               className={`${styles.tabBtn} ava-text-subheading ${activeTab === "manual" ? styles.tabBtnActive : ""}`}
//               onClick={() => handleTabChange("manual")}
//             >
//               Register Manually
//             </button>
//             <button
//               type="button"
//               className={`${styles.tabBtn} ${activeTab === "abha" ? styles.tabBtnActive : ""}`}
//               onClick={() => handleTabChange("abha")}
//             >
//               Register with ABHA
//             </button>
//           </div>

//           {/* Form */}
//           <form className={styles.form} onSubmit={handleSubmit} noValidate>

//             {activeTab === "manual" && (
//               <RegistrationFields
//                 values={values}
//                 errors={errors}
//                 onChange={handleChange}
//                 // onGender={handleGender}
//                 tab={activeTab}
//               />
//             )}

//             {activeTab === "abha" && (
//               <div className={`${styles.abhaSection}`}>
//                 <div className={styles.abhaSectionHeader}>
//                   <div>
//                     <p className={styles.abhaSectionTitle}>Link your health ID (ABHA)</p>
//                     <p className={styles.abhaSectionSub}>Recommended for a seamless experience</p>
//                   </div>
//                   <button type="button" className={`ava-text-caption ${styles.abhaWhatBtn}`}>
//                     ⓘ What is ABHA?
//                   </button>
//                 </div>

//                 <div className={`${styles.inputIconWrap} ${styles.abhaInputWrap}`}>
//                   <span className={`ava-text-caption ava-card-bg ${styles.abhaTag}`}>ABHA</span>
//                   <input
//                     className={styles.abhaInput}
//                     type="text" name="abhaNumber" value={values.abhaNumber}
//                     onChange={handleChange}
//                     placeholder="Enter your 14-digit ABHA number"
//                     maxLength={14}
//                     onKeyPress={(e) => { if (!/\d/.test(e.key)) e.preventDefault(); }}
//                   />
//                   <span className={styles.abhaOptional}>Optional</span>
//                 </div>

//                 {errors.abhaNumber && (
//                   <span className={styles.errorMsg}>{errors.abhaNumber}</span>
//                 )}
//               </div>
//             )}

//             {/* Terms */}
//             <div className={styles.formFooter}>
//             <label className={styles.termsRow}>
//               <input
//                 type="checkbox" className={styles.checkbox}
//                 checked={agreed} onChange={(e) => setAgreed(e.target.checked)}
//               />
//               <span className="ava-text-caption">
//                 I agree to the{" "}
//                 <Link to="#" className={styles.termsLink}>Terms of Use</Link>
//                 {" "}and{" "}
//                 <Link to="#" className={styles.termsLink}>Privacy Policy</Link>
//               </span>
//             </label>

//             <button type="submit" className={styles.submitBtn} disabled={loading}>
//               {loading ? "Creating account…" : "Create Account"}
//             </button>
//             </div>
//           </form>

//           {/* Footer */}
//           <p className={`ava-text-caption ${styles.footerText}`}>
//             By creating an account, you agree to our{" "}
//             <Link to="#" className={styles.termsLink}>Terms of Use</Link>
//             {" "}and{" "}
//             <Link to="#" className={styles.termsLink}>Privacy Policy.</Link>
//           </p>
//         </div>

//         {/* ── Mobile-only: photoCard + compliance shown below the form ── */}
//         <MobileBottomCard />

//       </main>
//     </div>
//   );
// }
